/**
 * Pritect Beacon verified bootstrap.
 *
 * The embed a site pastes once and never re-copies, WITH a browser-enforced
 * integrity check. It is published to an immutable, content-hashed path
 * (releases/pritect-beacon-bootstrap-v<major>.<hash>.min.js), so the snippet
 * can carry SRI and the browser refuses any altered byte of it.
 *
 * It does what the v1 loader does synchronously (prelude.ts): consent
 * defaults, the TCF stub, script blocking, the handoff. What differs is how it
 * picks the core. The loader has the core baked in, which is why the loader's
 * bytes change on every release and cannot carry SRI. The bootstrap instead
 * fetches the signed release pointer, verifies it against the public keys
 * compiled into it (release-pointer.json), and only then injects the core the
 * pointer names, with the integrity the pointer carries. Releases move the
 * pointer, not the snippet.
 *
 * Trackers are already blocked while the pointer is fetched, so the extra
 * round trip costs banner paint time, not consent. Repeat visits skip it: the
 * last verified pointer is cached, re-verified locally, and used at once while
 * a fresh one is fetched in the background for the next page view.
 *
 * Any failure fails closed: trackers stay blocked, no banner, pb:error fires,
 * and the missing window.PritectBeacon makes the next scan raise an alert. That
 * includes a page with no WebCrypto (plain http), where nothing can be
 * verified at all.
 */

import { failClosed, injectCore, resolveBucketUrl, startPrelude } from "../loader/prelude";
import {
  PointerError,
  readCachedPointer,
  readHighWater,
  rememberPointer,
  verifyPointer,
  type PointerKey,
  type PointerPayload,
} from "./pointer";
import releasePointer from "./release-pointer.json";

export const BOOTSTRAP_MAJOR: number = releasePointer.bootstrapMajor;
const KEYS = releasePointer.keys as PointerKey[];

/** Where the release pipeline publishes the pointer this bootstrap major reads. */
export const POINTER_PATH = "pointer/v1.json";

const FETCH_TIMEOUT_MS = 5000;
const RETRY_DELAY_MS = 1500;

function selfSrc(): string {
  if (document.currentScript instanceof HTMLScriptElement && document.currentScript.src) {
    return document.currentScript.src;
  }
  return document.querySelector<HTMLScriptElement>('script[src*="pritect-beacon-bootstrap"][data-domain-id]')?.src ?? "";
}

async function fetchPointerText(url: string): Promise<string> {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null;
  try {
    const res = await fetch(url, { credentials: "omit", signal: controller?.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.text();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function fetchVerified(
  url: string,
  subtle: SubtleCrypto,
): Promise<{ text: string; payload: PointerPayload }> {
  let text: string;
  try {
    text = await fetchPointerText(url);
  } catch {
    // One retry for a transient network blip. A verification failure below is
    // not retried: the same bytes would fail the same way.
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    text = await fetchPointerText(url);
  }
  const payload = await verifyPointer(text, KEYS, {
    subtle,
    bootstrapMajor: BOOTSTRAP_MAJOR,
    highWater: readHighWater(),
  });
  return { text, payload };
}

async function resolveAndInject(self: string): Promise<void> {
  const pointerUrl = resolveBucketUrl(POINTER_PATH, self);
  const subtle = typeof crypto !== "undefined" ? crypto.subtle : undefined;
  if (!subtle) {
    failClosed(
      pointerUrl,
      "This page cannot verify the Pritect Beacon release, because WebCrypto is unavailable (the page is probably not served over https).",
      "webcrypto_unavailable",
    );
    return;
  }

  const inject = (payload: PointerPayload) => injectCore(resolveBucketUrl(payload.core, self), payload.integrity);

  const cached = readCachedPointer();
  if (cached) {
    try {
      const payload = await verifyPointer(cached, KEYS, {
        subtle,
        bootstrapMajor: BOOTSTRAP_MAJOR,
        highWater: readHighWater(),
      });
      inject(payload);
      // Refresh for the next page view. Best effort: this page is already
      // running a verified core, so a failure here changes nothing now.
      void fetchVerified(pointerUrl, subtle)
        .then(({ text, payload: fresh }) => rememberPointer(text, fresh))
        .catch(() => undefined);
      return;
    } catch {
      // Expired, from a retired key, or tampered with locally: go to the network.
    }
  }

  try {
    const { text, payload } = await fetchVerified(pointerUrl, subtle);
    rememberPointer(text, payload);
    inject(payload);
  } catch (error) {
    if (error instanceof PointerError) {
      failClosed(pointerUrl, `Refused an unverifiable Pritect Beacon release: ${error.message}`, error.reason);
    } else {
      failClosed(pointerUrl, "Failed to fetch the Pritect Beacon release pointer.", "pointer_unreachable");
    }
  }
}

function boot(): void {
  // Captured before anything else: document.currentScript is only set while
  // this script is executing synchronously.
  const self = selfSrc();
  if (!startPrelude()) return;
  void resolveAndInject(self).catch(() => {
    failClosed(resolveBucketUrl(POINTER_PATH, self), "Failed to start the Pritect Beacon consent engine.");
  });
}

try {
  boot();
} catch {
  // A throw here would leave the page unblocked and unmanaged, so it is
  // swallowed rather than allowed to surface as an uncaught error.
}
